import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';

import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { AuthService } from '@sociuu/admin/auth/services/auth.service';
import { NotificationService } from '@utility/services/notification.service';
import { NetworkErrorHandlerService } from '@app/core/services/network-error-handler.service';

import { getValidationErrorMessage, isLinkedInConnectionExpiredError, LINKEDIN_CONNECTION_EXPIRED_MESSAGE } from '@app/utility/app.util';
import { captureApiError, captureNetworkError } from '@app/utility/sentry-error-handlers.utils';
import { environment } from 'assets/js/environments';

const BYPASS_ERROR_TYPES: string[] = [
  'InvalidCompanyNameException',
  'PendingScheduledSharesException',
];

const LINKEDIN_SOCIAL_CONNECT_URL_PART = '/v2/social-connect/linkedin/';

@Injectable()
export class ApiRequestInterceptor implements HttpInterceptor {
  private helper = new JwtHelperService();

  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private networkErrorHandler: NetworkErrorHandlerService,
    private transloco: TranslocoService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let token: string = '';

    if (request.url.includes('/sociuuhub/become') && !this.authService.isSociuuhub) {
      token = localStorage.getItem('token');
    }
    else if (
      (request.url.includes('/sociuuhub') && !request.url.includes('/v2/settings/sociuuhub_enable_ai')) || this.authService.isSociuuhub) {
      token = localStorage.getItem('tokenUser');
    }
    else {
      token = localStorage.getItem('token');
    }

    if (
      token &&
      !request.url.includes('getPreview') &&
      !request.url.includes('/v2/magic-login')
    ) request = this.addToken(request, token);

    let req: HttpRequest<any> = request;
    const setHeaders: Record<string, string> = {};

    if (this.shouldSendTenantOrigin(request)) {
      setHeaders['X-Tenant-Origin'] = environment.origin;
    }

    if (Object.keys(setHeaders).length > 0) {
      req = request.clone({
        setHeaders,
      });
    }

    return next.handle(req).pipe(
      catchError((error) => {
        // Log the error for debugging
        console.error('HTTP Error intercepted:', error);

        // Handle XMLHttpRequestProgressEvent and network errors
        if (this.networkErrorHandler.shouldHandleError(error)) {
          const errorMessage = this.networkErrorHandler.getErrorMessage(error);
          this.notificationService.showError(errorMessage);

          // Capture network error with proper categorization
          captureNetworkError(req.method, req.url, errorMessage, { payload: req.body });

          return throwError(() => new Error(errorMessage));
        }

        if (error instanceof HttpErrorResponse) {
          // Handle CORS errors (status 0)
          if (error.status === 0) {
            const corsErrorMessage = 'CORS Error: Unable to reach the API server. Please check your network connection and API configuration.';
            this.notificationService.showError(corsErrorMessage);

            // Capture CORS error as network error
            captureNetworkError(req.method, req.url, corsErrorMessage, {
              payload: req.body,
              errorType: 'CORS',
              status: 0
            });

            return throwError(() => new Error(corsErrorMessage));
          }

          const errorMessage = error.error.message
            ? error.error.message
            : error.error.messages
              ? error.error.messages?.[0]
              : error.message
                ? error.message
                : error.error;

          // Capture all HTTP errors with proper categorization
          captureApiError(
            req.method,
            req.url,
            error.status,
            errorMessage,
            {
              payload: req.body,
              response: error.error,
              statusText: error.statusText,
              errorDetails: error.error,
            }
          );

          if ([400, 403].includes(error.status)) {
            if (error?.error?.reason) {
              return throwError(() => error.error);
            }
            return throwError(() => getValidationErrorMessage(error.error));
          }

          if (error.status === 404) {
            this.notificationService.showError(getValidationErrorMessage(error.error));
          }

          if (error.status === 500) {
            this.notificationService.showError(error.message);
            this.router.navigate(['/error/500']);
          }

          if ([401, 423].includes(error.status)) {
            if (req.url.includes(LINKEDIN_SOCIAL_CONNECT_URL_PART) && isLinkedInConnectionExpiredError(error.error)) {
              return throwError(() => ({
                ...error.error,
                error_type: error.error?.error_type || 'InvalidAccessTokenException',
                message: LINKEDIN_CONNECTION_EXPIRED_MESSAGE,
              }));
            }

            this.authService.getClientInfo().subscribe();
            this.notificationService.showError(getValidationErrorMessage(error.error) || 'You are unauthorized');
            if (this.helper.decodeToken(token)?.sociuuhub_login || this.router.url.includes('/sociuuhub')) {
              localStorage.removeItem('tokenUser');
              this.router.navigate(['/sociuuhub/login']);
            } else {
              localStorage.removeItem('token');
              if (!this.router.url.includes('/public/')) {
                this.router.navigate(['/login']);
              }
            }
          }

          if ([410, 415, 422, 503, 409].includes(error.status)) {
            if (error.status === 409 && error.error?.error_type === 'SourceRetrospectiveOperationActiveException') {
              this.notificationService.showError(this.transloco.translate('This source is being updated. Try again after the update finishes.'));
            }

            if (req.url.includes(LINKEDIN_SOCIAL_CONNECT_URL_PART) && isLinkedInConnectionExpiredError(error.error)) {
              return throwError(() => ({
                ...error.error,
                error_type: error.error?.error_type || 'InvalidAccessTokenException',
                message: LINKEDIN_CONNECTION_EXPIRED_MESSAGE,
              }));
            }

            if (error.status === 422 && !BYPASS_ERROR_TYPES.includes(error.error?.error_type)) {
              this.notificationService.showError(getValidationErrorMessage(error.error));
            }
            return throwError(() => error.error);
          }

          this.notificationService.showError(getValidationErrorMessage(error.error));
          return throwError(() => error.error);
        } else {
          // Handle non-HTTP errors (like network failures, XMLHttpRequestProgressEvent, etc.)
          console.error('Non-HTTP error:', error);

          // Use network error handler for better error management
          if (this.networkErrorHandler.shouldHandleError(error)) {
            const errorMessage = this.networkErrorHandler.getErrorMessage(error);
            this.notificationService.showError(errorMessage);
            return throwError(() => new Error(errorMessage));
          }

          // Generic error handling for other types of errors
          this.notificationService.showError('An unexpected error occurred. Please try again.');
          return throwError(() => new Error(error));
        }
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldSendTenantOrigin(request: HttpRequest<any>): boolean {
    if (environment.env !== 'local' || !environment.origin) {
      return false;
    }

    const targetUrls = [
      environment.apiUrl,
      environment.baseUrl,
      environment.sociuuhubApiUrl,
    ].filter(Boolean);

    return targetUrls.some((url) => request.url.startsWith(url));
  }
}
